import { useState } from 'react';
import { Link } from 'react-router';
import clsx from 'clsx';
import {
  PHASE_HINTS,
  PHASE_LABELS,
  RETRO_PHASES,
  TOTAL_STEPS,
  nextPhase,
  phaseIndex,
  prevPhase,
  stepNumber,
  transitionNeedsConfirmation,
  type RetroPhase,
  type RetroSnapshot,
} from '@retro/shared';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  FileText,
  Lightbulb,
  ListTodo,
  Lock,
  LogOut,
  Menu,
  MoreHorizontal,
  Settings,
  Users,
} from 'lucide-react';
import { useRetroStore, useVotesUsed } from '../../lib/retroStore.js';
import { Modal } from '../ui.js';
import { TimerWidget } from './TimerWidget.js';
import { ParticipantList } from './ParticipantList.js';
import { InviteDialog } from './InviteDialog.js';
import { SettingsPanel } from './SettingsPanel.js';
import { CommentsPhase } from './CommentsPhase.js';
import { GroupPhase } from './GroupPhase.js';
import { VotePhase } from './VotePhase.js';
import { ActionPlanPhase } from './ActionPlanPhase.js';
import { ReportPhase } from './ReportPhase.js';
import { CarriedOverBanner } from './CarriedOverBanner.js';

const PHASE_EMOJI: Record<RetroPhase, string> = {
  COMMENTS: '💡',
  GROUP: '🧩',
  VOTE: '👍',
  ACTION_PLAN: '📋',
  CLOSED: '🏁',
};

const TIPS: Record<RetroPhase, string[]> = {
  COMMENTS: [
    'Nobody can see anyone else’s comments yet — write freely.',
    'One idea per card keeps the grouping step easy.',
    'Use the author menu to post anonymously when a topic feels risky.',
  ],
  GROUP: [
    'Drag a card onto another to group duplicates together.',
    'Give each group a short name so the vote step reads clearly.',
    'Attach topics to spot themes that keep coming back.',
  ],
  VOTE: [
    'Spend your votes on what you most want to talk about.',
    'You can put several votes on one card.',
    'Nobody can see who voted for what — not even the facilitator.',
  ],
  ACTION_PLAN: [
    'Work down from the most-voted cards.',
    'Give every action one owner, so progress has a name against it.',
    'Small and specific beats ambitious and vague.',
  ],
  CLOSED: [
    'Rate the session so facilitation keeps improving.',
    'Actions carry over to the team board automatically.',
  ],
};

function PhaseBanner({ snapshot }: { snapshot: RetroSnapshot }) {
  const used = useVotesUsed();
  const { votesPerParticipant } = snapshot.votes;
  const votesLeft = Math.max(0, votesPerParticipant - used);

  const content: Record<RetroPhase, { icon: typeof EyeOff; body: React.ReactNode }> = {
    COMMENTS: {
      icon: EyeOff,
      body: (
        <>
          <strong>Private writing.</strong> Your cards stay hidden from everyone until the
          facilitator starts Group. Others only see how many cards exist.
        </>
      ),
    },
    GROUP: {
      icon: Users,
      body: (
        <>
          <strong>Cards are now visible to the team.</strong> Group duplicates with
          “Group with…”. Anonymous authors stay hidden.
        </>
      ),
    },
    VOTE: {
      icon: Lock,
      body: (
        <>
          <strong>Voting is private.</strong> Nobody, including the facilitator, can see who voted
          for what. You have {votesLeft} {votesLeft === 1 ? 'vote' : 'votes'} left.{' '}
          <span className="budget" aria-hidden="true">
            {Array.from({ length: votesPerParticipant }, (_, i) => (
              <i key={i} className={i < votesLeft ? 'on' : undefined} />
            ))}
          </span>
        </>
      ),
    },
    ACTION_PLAN: {
      icon: ListTodo,
      body: (
        <>
          <strong>Turn the top topics into owned actions.</strong> Each action needs an owner.
        </>
      ),
    },
    CLOSED: {
      icon: FileText,
      body: (
        <>
          <strong>Session closed.</strong> The summary is visible to the team. Anonymous authors
          stay anonymous.
        </>
      ),
    },
  };

  const { icon: Icon, body } = content[snapshot.retro.phase];
  return (
    <div className="banner" role="note">
      <Icon size={16} className="i flex-none" aria-hidden />
      <span>{body}</span>
    </div>
  );
}

export function RetroBoard({ snapshot, isSignedIn }: { snapshot: RetroSnapshot; isSignedIn: boolean }) {
  const send = useRetroStore((state) => state.send);
  const status = useRetroStore((state) => state.status);

  const [showParticipants, setShowParticipants] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [railOpen, setRailOpen] = useState(true);
  const [confirmTo, setConfirmTo] = useState<RetroPhase | null>(null);

  const { retro, me } = snapshot;
  const phase = retro.phase;
  const isFacilitator = me.isFacilitator;
  const isGuest = !isSignedIn;

  const next = nextPhase(phase);
  const previous = prevPhase(phase);
  const closed = phase === 'CLOSED';

  function goTo(target: RetroPhase) {
    setShowSteps(false);
    if (transitionNeedsConfirmation(phase, target)) {
      setConfirmTo(target);
      return;
    }
    void send('retro:advancePhase', { from: phase, to: target });
  }

  const total = snapshot.participants.length;
  const statusText =
    phase === 'VOTE'
      ? `${snapshot.votes.participantsFinished} of ${total} finished voting`
      : `${snapshot.readyCount} / ${total} ready`;

  const readyButton = closed ? null : (
    <button
      type="button"
      onClick={() => void send('retro:setReady', { isReady: !me.isReady })}
      className={clsx('btn', me.isReady && 'btn-ok')}
      aria-pressed={me.isReady}
    >
      <Check size={15} aria-hidden />
      <span className="hidden sm:inline">{me.isReady ? 'Ready' : 'Mark as ready'}</span>
      <span className="sm:hidden">{me.isReady ? 'Ready' : 'I’m ready'}</span>
    </button>
  );

  const nextControl = closed ? (
    <Link to={isSignedIn ? '/' : '/login'} className="btn btn-primary">
      {isSignedIn ? 'Back to overview' : 'Leave retrospective'}
    </Link>
  ) : isFacilitator ? (
    <button
      type="button"
      className="btn btn-primary"
      onClick={() => next && goTo(next)}
      aria-label={next ? `Go to ${PHASE_LABELS[next]}` : 'Done'}
      disabled={!next}
    >
      Next: {next ? PHASE_LABELS[next] : 'Done'} <ChevronRight size={15} aria-hidden />
    </button>
  ) : (
    // The room moves when the facilitator moves it — the disabled button still
    // shows which step comes next.
    <button
      type="button"
      className="btn btn-primary"
      disabled
      aria-label={next ? `Go to ${PHASE_LABELS[next]}` : 'Done'}
      title="The facilitator moves to the next step"
    >
      Next: {next ? PHASE_LABELS[next] : 'Done'} <ChevronRight size={15} aria-hidden />
    </button>
  );

  const backControl = (
    <button
      type="button"
      className="btn"
      disabled={!isFacilitator || !previous || closed}
      onClick={() => previous && goTo(previous)}
      aria-label={previous ? `Go to ${PHASE_LABELS[previous]}` : 'Back'}
    >
      <ChevronLeft size={15} aria-hidden />
      {previous && !closed ? PHASE_LABELS[previous] : 'Back'}
    </button>
  );

  return (
    <div className="sess h-dvh">
      <h2 className="sr-only" tabIndex={-1}>
        {retro.name}, {PHASE_LABELS[phase]} step
      </h2>

      {status === 'reconnecting' ? (
        <div className="bg-amber-500 px-3 py-1 text-center text-xs font-medium text-white">
          Reconnecting…
        </div>
      ) : null}

      {/* ------------------------------------------------------- top bar */}
      <header className="sess-top">
        {/* Phone-only menu button: carries the rename/leave/settings actions. */}
        <button
          type="button"
          className="icon-btn lg:hidden"
          onClick={() => setShowMenu(true)}
          aria-label="Retrospective menu"
        >
          <Menu size={20} />
        </button>

        {isGuest ? (
          <span className="grid size-8 flex-none place-items-center rounded-[9px] bg-navy text-[13px] font-extrabold text-white max-lg:hidden">
            r.
          </span>
        ) : (
          <Link to="/" className="btn btn-ghost btn-sm max-lg:hidden">
            <ChevronLeft size={16} aria-hidden /> Overview
          </Link>
        )}

        <div className="sess-title">
          <strong className="truncate">
            {retro.name}
            {isGuest ? <span className="badge badge-warn ml-2 align-middle">Guest</span> : null}
          </strong>
          <span className="truncate">
            {retro.templateName} · {retro.teamName} · Step {stepNumber(phase)} / {TOTAL_STEPS}
          </span>
        </div>

        <ol className="steps max-lg:hidden" aria-label="Session steps">
          {RETRO_PHASES.map((candidate) => {
            const index = phaseIndex(candidate);
            const done = index < phaseIndex(phase);
            const current = candidate === phase;
            const inner = (
              <>
                <span className="n" aria-hidden>
                  {done ? <Check size={11} /> : index + 1}
                </span>
                {PHASE_LABELS[candidate]}
                {done ? <span className="sr-only"> (completed)</span> : null}
              </>
            );
            return (
              <li key={candidate}>
                {isFacilitator ? (
                  <button
                    type="button"
                    className={clsx('step', done && 'step-done')}
                    aria-current={current ? 'step' : undefined}
                    title={`Go to ${PHASE_LABELS[candidate]}`}
                    // The current pill is the "Change step" affordance the tests
                    // (and regulars) look for.
                    aria-label={current ? 'Change step' : undefined}
                    onClick={() => !current && goTo(candidate)}
                    disabled={closed}
                  >
                    {inner}
                  </button>
                ) : (
                  <span
                    className={clsx('step', done && 'step-done')}
                    aria-current={current ? 'step' : undefined}
                  >
                    {inner}
                  </span>
                )}
              </li>
            );
          })}
        </ol>

        <div className="sess-actions">
          {!closed ? <TimerWidget timer={retro.timer} canControl={isFacilitator} /> : null}
          <button
            type="button"
            className="icon-btn max-lg:hidden"
            onClick={() => setShowMenu(true)}
            aria-label="Retrospective menu"
            title="Retrospective menu"
          >
            <MoreHorizontal size={18} />
          </button>

          {isGuest ? (
            <Link to="/login" className="btn btn-sm max-lg:hidden">
              <LogOut size={15} aria-hidden /> Leave
            </Link>
          ) : null}
          {isFacilitator && !closed ? (
            <>
              <button
                type="button"
                className="btn btn-sm max-lg:hidden"
                onClick={() => setShowInvite(true)}
              >
                <Users size={15} aria-hidden /> Invite
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm max-lg:hidden"
                onClick={() => setConfirmTo('CLOSED')}
              >
                End session
              </button>
              <button
                type="button"
                className="icon-btn max-lg:hidden"
                onClick={() => setShowSettings(true)}
                aria-label="Session settings"
                title="Session settings"
              >
                <Settings size={17} />
              </button>
            </>
          ) : null}
        </div>
      </header>

      {/* ------------------------------------------------- mobile step strip */}
      {isFacilitator ? (
        <button
          type="button"
          className="m-step lg:hidden"
          onClick={() => setShowSteps(true)}
          aria-label="Change step"
        >
          <span>
            <span className="k">
              Step {stepNumber(phase)} / {TOTAL_STEPS}
            </span>
            <strong>
              {PHASE_EMOJI[phase]} {PHASE_LABELS[phase]}
            </strong>
          </span>
          <span className="chev">
            Change step <ChevronRight size={14} aria-hidden />
          </span>
          <span className="meter" aria-hidden>
            <span style={{ width: `${(stepNumber(phase) / TOTAL_STEPS) * 100}%` }} />
          </span>
        </button>
      ) : (
        <div className="m-step lg:hidden">
          <span>
            <span className="k">
              Step {stepNumber(phase)} / {TOTAL_STEPS}
            </span>
            <strong>
              {PHASE_EMOJI[phase]} {PHASE_LABELS[phase]}
            </strong>
          </span>
          {isGuest ? <span className="badge badge-warn">Guest</span> : null}
          <span className="meter" aria-hidden>
            <span style={{ width: `${(stepNumber(phase) / TOTAL_STEPS) * 100}%` }} />
          </span>
        </div>
      )}

      {/* -------------------------------------------------------- the board */}
      <div className="sess-body">
        <main className="sess-main" data-testid="board-scroll">
          <div className="sess-content">
            <PhaseBanner snapshot={snapshot} />

            {phase === 'COMMENTS' && snapshot.carriedOverActions.length > 0 ? (
              <CarriedOverBanner actions={snapshot.carriedOverActions} canEdit={isFacilitator} />
            ) : null}

            {phase === 'COMMENTS' ? <CommentsPhase snapshot={snapshot} /> : null}
            {phase === 'GROUP' ? <GroupPhase snapshot={snapshot} /> : null}
            {phase === 'VOTE' ? <VotePhase snapshot={snapshot} /> : null}
            {phase === 'ACTION_PLAN' ? <ActionPlanPhase snapshot={snapshot} /> : null}
            {phase === 'CLOSED' ? <ReportPhase snapshot={snapshot} isSignedIn={isSignedIn} /> : null}
          </div>
        </main>

        {!closed ? (
          railOpen ? (
            <aside className="rail max-lg:hidden" aria-label="Participants">
              <div className="rail-h">
                <h3>Participants · {total}</h3>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRailOpen(false)}>
                  Hide
                </button>
              </div>
              {isFacilitator ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm btn-block mb-3"
                  onClick={() => setShowInvite(true)}
                >
                  <Users size={15} aria-hidden /> Invite people
                </button>
              ) : null}
              <ParticipantList snapshot={snapshot} />
            </aside>
          ) : (
            <button
              type="button"
              className="rail-collapsed max-lg:hidden"
              onClick={() => setRailOpen(true)}
              aria-label={`Show participants, ${snapshot.readyCount} of ${total} ready`}
            >
              <Users size={18} aria-hidden />
              <span>
                {snapshot.readyCount}/{total}
              </span>
              <ChevronLeft size={14} aria-hidden />
            </button>
          )
        ) : null}
      </div>

      {/* --------------------------------------------------------- footers */}
      <footer className="sess-foot max-lg:hidden">
        <div className="sess-pill">
          {backControl}
          {closed ? null : (
            <>
              {readyButton}
              <span className="foot-status" aria-live="polite">
                {statusText}
              </span>
            </>
          )}
          {closed && isFacilitator ? (
            <button
              type="button"
              className="btn"
              onClick={() => setConfirmTo('ACTION_PLAN')}
            >
              Reopen retrospective
            </button>
          ) : null}
          {nextControl}
        </div>
      </footer>

      <footer className="m-sess-foot lg:hidden">
        {!closed ? (
          <button
            type="button"
            className="btn"
            onClick={() => setShowParticipants(true)}
            aria-label={`${total} participants`}
          >
            <Users size={16} aria-hidden />
            {snapshot.readyCount}/{total}
          </button>
        ) : null}
        {!closed ? readyButton : null}
        {closed && isFacilitator ? (
          <button type="button" className="btn" onClick={() => setConfirmTo('ACTION_PLAN')}>
            Reopen retrospective
          </button>
        ) : null}
        {nextControl}
      </footer>

      {/* --------------------------------- overlays -------------------------------- */}

      <Modal open={showParticipants} onClose={() => setShowParticipants(false)} title="Who’s here">
        <ParticipantList snapshot={snapshot} />
        {isFacilitator && !closed ? (
          <button
            type="button"
            className="btn btn-primary btn-block mt-4"
            onClick={() => {
              setShowParticipants(false);
              setShowInvite(true);
            }}
          >
            Invite people
          </button>
        ) : null}
      </Modal>

      <Modal open={showSteps} onClose={() => setShowSteps(false)} title="Jump to a step" width="sm">
        <ul className="menu-list">
          {RETRO_PHASES.map((candidate) => {
            const isCurrent = candidate === phase;
            return (
              <li key={candidate}>
                <button
                  type="button"
                  disabled={isCurrent || closed}
                  onClick={() => goTo(candidate)}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  <span aria-hidden>{PHASE_EMOJI[candidate]}</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">
                      {stepNumber(candidate)}. {PHASE_LABELS[candidate]}
                      {isCurrent ? ' — current' : ''}
                    </span>
                    <span className="block text-[12px] font-normal text-muted">
                      {PHASE_HINTS[candidate]}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </Modal>

      <Modal open={showTips} onClose={() => setShowTips(false)} title={`Tips — ${PHASE_LABELS[phase]}`} width="sm">
        <ul className="bul">
          {TIPS[phase].map((tip) => (
            <li key={tip}>{tip}</li>
          ))}
        </ul>
      </Modal>

      <Modal open={showMenu} onClose={() => setShowMenu(false)} title={retro.name} width="sm">
        <p className="text-sm text-muted">
          {retro.teamName} · {retro.templateName}
        </p>

        {isFacilitator ? (
          <label className="field-l mt-4">
            Rename
            <input
              className="input"
              defaultValue={retro.name}
              maxLength={120}
              onBlur={(event) => {
                const value = event.target.value.trim();
                if (value && value !== retro.name) void send('retro:rename', { name: value });
              }}
            />
          </label>
        ) : null}

        <ul className="menu-list mt-3">
          {isSignedIn ? (
            <li>
              <Link to={`/teams/${retro.teamId}`}>
                <Users size={18} aria-hidden /> Team board &amp; history
              </Link>
            </li>
          ) : null}
          {isFacilitator && !closed ? (
            <>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setShowMenu(false);
                    setShowInvite(true);
                  }}
                >
                  <Users size={18} aria-hidden /> Invite people
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setShowMenu(false);
                    setShowSettings(true);
                  }}
                >
                  <Lightbulb size={18} aria-hidden /> Session settings
                </button>
              </li>
            </>
          ) : null}
          <li>
            <button
              type="button"
              onClick={() => {
                setShowMenu(false);
                setShowTips(true);
              }}
            >
              <Lightbulb size={18} aria-hidden /> Tips for this step
            </button>
          </li>
          <li>
            {/* A signed-in member stays a participant of retros they have joined,
                so there is nothing to "leave" — the honest action is just to go
                back. A guest genuinely leaves: this is their only way out. */}
            <Link to={isSignedIn ? '/' : '/login'}>
              <ChevronLeft size={18} aria-hidden />
              {isSignedIn ? 'Back to overview' : 'Leave retrospective'}
            </Link>
          </li>
        </ul>
      </Modal>

      <Modal
        open={confirmTo !== null}
        onClose={() => setConfirmTo(null)}
        width="sm"
        title={
          confirmTo === 'CLOSED'
            ? 'Close this retrospective?'
            : confirmTo === 'ACTION_PLAN'
              ? 'Reopen this retrospective?'
              : 'Reveal all comments?'
        }
        footer={
          <>
            <button type="button" className="btn" onClick={() => setConfirmTo(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                if (confirmTo) void send('retro:advancePhase', { from: phase, to: confirmTo });
                setConfirmTo(null);
              }}
            >
              {confirmTo === 'CLOSED'
                ? 'Close retro'
                : confirmTo === 'ACTION_PLAN'
                  ? 'Reopen retro'
                  : 'Reveal comments'}
            </button>
          </>
        }
      >
        {confirmTo === 'CLOSED' ? (
          <p className="text-sm text-muted">
            The board becomes read-only and the summary is published to the team. Actions stay
            editable on the team board.
          </p>
        ) : confirmTo === 'ACTION_PLAN' ? (
          <p className="text-sm text-muted">
            Reopening puts the retrospective back into the Action Plan step so the team can keep
            editing actions. The summary will no longer be visible.
          </p>
        ) : (
          <p className="text-sm text-muted">
            Everyone will see every comment written so far. There’s no way to un-see them, so check
            that nobody is still typing.
          </p>
        )}
      </Modal>

      <InviteDialog retroId={retro.id} open={showInvite} onClose={() => setShowInvite(false)} />

      {isFacilitator ? (
        <SettingsPanel
          settings={retro.settings}
          columns={snapshot.columns}
          open={showSettings}
          onClose={() => setShowSettings(false)}
        />
      ) : null}
    </div>
  );
}
